// src/components/Auth/UnlockPage.tsx
import React, { useState, useEffect } from 'react';
import { Lock, Fingerprint, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';

interface UnlockPageProps {
  userId: string;
  onUnlock: (password?: string) => Promise<void>;
}

export const UnlockPage: React.FC<UnlockPageProps> = ({ userId, onUnlock }) => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [hasFingerprint, setHasFingerprint] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  useEffect(() => {
    const checkEnvironment = async () => {
      // 1. 檢查本地是否有 device_key
      const storageKey = `device_key_${userId}`;
      const deviceKey = localStorage.getItem(storageKey);

      // 2. 檢查生物辨識硬體支援
      let isHardwareSupported = false;
      if (window.PublicKeyCredential) {
        try {
          isHardwareSupported = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        } catch {
          isHardwareSupported = false;
        }
      }

      const canUseFingerprint = !!deviceKey && isHardwareSupported;
      setHasFingerprint(canUseFingerprint);

      // 3. 判斷是否為行動裝置 (Mobile / Tablet)
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      // 🎯 核心體驗策略：
      // 電腦版 OR 未註冊指紋 ➔ 優先顯示密碼輸入框
      // 只有「行動裝置 + 已註冊指紋」才優先顯示指紋按鈕
      if (!isMobile || !canUseFingerprint) {
        setShowPasswordInput(true);
      }
    };

    checkEnvironment();
  }, [userId]);

  // 指紋解鎖
  const handleFingerprintUnlock = async () => {
    setIsLoading(true);
    try {
      await onUnlock();
    } catch {
      setShowPasswordInput(true);
    } finally {
      setIsLoading(false);
    }
  };

  // 密碼解鎖
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error('請輸入密碼');
      return;
    }

    setIsLoading(true);
    try {
      await onUnlock(password);
    } catch {
      // 錯誤處理由 App.tsx 的 toast 呈現
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full mb-4">
            <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">NOTEBOOK++</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-2">應用已鎖定</p>
        </div>

        <div className="space-y-4">
          {/* 密碼解鎖區塊（電腦版預設展開 / 行動端點擊展開） */}
          {showPasswordInput ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="請輸入主密碼"
                autoFocus
                disabled={isLoading}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? '解鎖中...' : '密碼解鎖'}
              </button>

              {/* 電腦版如果有指紋，下方保留切換指紋按鈕 */}
              {hasFingerprint && (
                <button
                  type="button"
                  onClick={handleFingerprintUnlock}
                  disabled={isLoading}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 py-2 flex items-center justify-center gap-2"
                >
                  <Fingerprint className="w-4 h-4" />
                  使用 Touch ID / 指紋快捷解鎖
                </button>
              )}
            </form>
          ) : (
            /* 行動端且有指紋時預設呈現的大圖示按鈕 */
            <>
              <button
                onClick={handleFingerprintUnlock}
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium py-3.5 px-4 rounded-lg transition-all flex items-center justify-center gap-3 text-lg disabled:opacity-50"
              >
                <Fingerprint className="w-6 h-6" />
                {isLoading ? '驗證中...' : '指紋快捷解鎖'}
              </button>

              <button
                type="button"
                onClick={() => setShowPasswordInput(true)}
                className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 py-2 flex items-center justify-center gap-2"
              >
                <KeyRound className="w-4 h-4" />
                改用密碼解鎖
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};